import * as React from "react"
import { useState, useRef, useEffect } from "react"
import "./style_1.scss"
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import { toLower, capitalize } from 'lodash'
import { countryCoordinates, countryCoordinates3 } from '/src/components/constants'

let mapboxgl = null;
if (typeof window !== 'undefined') {
    try {
        mapboxgl = require('mapbox-gl');
    } catch (error) {
    }
}

export default function ProductContentStyle1({ items, itemData }) {
    const [selectedJourneyIndex, setSelectedJourneyIndex] = useState(0);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [currentItemIndex, setCurrentItemIndex] = useState(0);
    const [mapError, setMapError] = useState(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [mapboxReady, setMapboxReady] = useState(false);

    const dropdownRef = useRef(null);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const swiperRef = useRef(null);
    const itemsSwiperRefs = useRef([]);
    const hasUserNavigatedRef = useRef(false);

    const hasProductJourney = itemData?.product_journey || items?.product_journey || items?.[0]?.product_journey;

    useEffect(() => {
        if (typeof window === 'undefined' || !hasProductJourney || mapboxReady) return;

        if (mapboxgl) {
            const cssLink = document.createElement('link');
            cssLink.rel = 'stylesheet';
            cssLink.href = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css';
            if (!document.querySelector('link[href*="mapbox-gl"]')) {
                document.head.appendChild(cssLink);
            }

            mapboxgl.accessToken = process.env.GATSBY_MAPBOX_ACCESS_TOKEN ||
                'pk.eyJ1IjoiZHJvc2NhIiwiYSI6ImNtZWNwZWxmODAxcWUyanNnZ24ydWNnZmYifQ.I74IPqX2mXe9gH-B2Rz5gA';
            setMapboxReady(true);
        } else {
            setMapError('Mapbox GL failed to load from npm package');
        }
    }, [hasProductJourney, mapboxReady]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Check if step should be visible in swiper
    const isStepVisible = (step) => {
        if (step?.display === 'false') return false;
        return !step.info?.every(item => !item.label);
    };

    const handleJourneySelect = (index) => {
        setSelectedJourneyIndex(index);
        setIsDropdownOpen(false);
        setCurrentStepIndex(0);
        setMapError(null);
        setIsMapLoaded(false);
        swiperRef.current?.slideTo(0, 0);
    };

    // Reset to first slide when data changes
    useEffect(() => {
        swiperRef.current?.slideTo(0, 0);
        setCurrentStepIndex(0);
        hasUserNavigatedRef.current = false;
    }, [selectedJourneyIndex]);

    const handleSlideClick = (swiperInstance) => {
        if (!swiperInstance) return;
        const { activeIndex, slides } = swiperInstance;
        if (activeIndex === slides.length - 1) {
            swiperInstance.slideTo(0);
        } else {
            swiperInstance.slideNext();
        }
    };

    const highlightCurrentStep = (visibleStepIndex, shouldCenter = true) => {
        if (!mapInstanceRef.current?._customMarkers) return;

        const productJourney = itemData?.product_journey || items?.product_journey || items?.[0]?.product_journey;
        const selectedJourney = productJourney[selectedJourneyIndex];
        const visibleSteps = selectedJourney?.steps?.filter(isStepVisible) || [];
        const currentStep = visibleSteps[visibleStepIndex];

        if (!currentStep) return;

        const currentCountry = currentStep.map_location;
        let activeMarkerCoordinates = null;

        mapInstanceRef.current._customMarkers.forEach(({ element, country, marker }) => {
            const dot = element.querySelector('.marker-dot');
            if (dot) {
                if (country === currentCountry) {
                    dot.classList.add('active');
                    activeMarkerCoordinates = marker.getLngLat();
                } else {
                    dot.classList.remove('active');
                }
            }
        });

        if (shouldCenter && activeMarkerCoordinates) {
            mapInstanceRef.current.flyTo({
                center: [activeMarkerCoordinates.lng, activeMarkerCoordinates.lat],
                zoom: Math.max(mapInstanceRef.current.getZoom(), 4),
                duration: 1000,
                essential: true
            });
        }
    };

    // Highlight step when current step changes
    useEffect(() => {
        if (mapInstanceRef.current?._customMarkers && isMapLoaded) {
            highlightCurrentStep(currentStepIndex, hasUserNavigatedRef.current);
        }
    }, [currentStepIndex, selectedJourneyIndex, isMapLoaded]);

    const createMap = async () => {
        try {
            if (typeof window === 'undefined' || !mapboxgl || !mapboxReady || !mapRef.current || !hasProductJourney) return;

            const productJourney = itemData?.product_journey || items?.product_journey || items?.[0]?.product_journey;
            const selectedJourney = productJourney[selectedJourneyIndex];
            if (!selectedJourney?.steps) return;

            // Clean existing map
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }

            // Get visible steps and create country mapping
            const visibleSteps = selectedJourney.steps.filter(isStepVisible);
            const countryMap = new Map();

            selectedJourney.steps.forEach((step, stepIndex) => {
                const countryCode = step.map_location;
                const countryData = countryCoordinates[countryCode] || countryCoordinates3[countryCode];
                if (!countryData?.loc) return;

                const isVisible = isStepVisible(step);
                const visibleIndex = isVisible ? visibleSteps.findIndex(s => s === step) : -1;

                if (!countryMap.has(countryCode)) {
                    countryMap.set(countryCode, {
                        country: countryCode,
                        coordinates: countryData.loc,
                        stepCount: 1,
                        isInteractive: isVisible,
                        visibleIndex: visibleIndex
                    });
                } else {
                    const existing = countryMap.get(countryCode);
                    existing.stepCount++;
                    if (isVisible && !existing.isInteractive) {
                        existing.isInteractive = true;
                        existing.visibleIndex = visibleIndex;
                    }
                }
            });

            const countries = Array.from(countryMap.values());
            if (countries.length === 0) return;

            const coordinates = countries.map(c => c.coordinates);
            const lngs = coordinates.map(coord => coord[0]);
            const lats = coordinates.map(coord => coord[1]);
            const bounds = [
                [Math.min(...lngs) - 5, Math.min(...lats) - 5],
                [Math.max(...lngs) + 5, Math.max(...lats) + 5]
            ];

            const map = new mapboxgl.Map({
                container: mapRef.current,
                style: process.env.GATSBY_MAPBOX_ACCESS_STYLE || 'mapbox://styles/mapbox/streets-v12',
                projection: 'mercator',
                bounds,
                fitBoundsOptions: { padding: 20 }
            });

            mapInstanceRef.current = map;

            map.on('load', () => {
                setIsMapLoaded(true);
                setMapError(null);

                const markers = countries.map(countryData => {
                    const markerEl = document.createElement('div');
                    const sizeClass = countryData.stepCount > 3 ? 'large' : countryData.stepCount > 1 ? 'medium' : 'small';
                    const interactiveClass = countryData.isInteractive ? 'interactive' : 'non-interactive';

                    markerEl.className = 'journey-marker';
                    markerEl.innerHTML = `<div class="marker-dot ${sizeClass} ${interactiveClass}"></div>`;
                    markerEl.style.cursor = countryData.isInteractive ? 'pointer' : 'default';

                    if (countryData.isInteractive) {
                        markerEl.addEventListener('click', (e) => {
                            e.stopPropagation();
                            if (swiperRef.current && countryData.visibleIndex >= 0) {
                                hasUserNavigatedRef.current = true;
                                swiperRef.current.slideTo(countryData.visibleIndex);
                                setCurrentStepIndex(countryData.visibleIndex);
                                highlightCurrentStep(countryData.visibleIndex, true);
                            }
                        });
                    }

                    const marker = new mapboxgl.Marker(markerEl)
                        .setLngLat(countryData.coordinates)
                        .addTo(map);

                    return { marker, element: markerEl, country: countryData.country };
                });

                map._customMarkers = markers;
            });

            map.on('error', (e) => {
                if (e.error?.message?.includes('style') || e.error?.message?.includes('Unauthorized')) {
                    try {
                        map.setStyle('mapbox://styles/mapbox/streets-v12');
                    } catch (fallbackError) {
                        setMapError('Unable to load any map style');
                    }
                } else {
                    setMapError(e.error?.message || 'Map failed to load');
                }
            });

        } catch (error) {
            setMapError(error.message || 'Failed to create map');
            setIsMapLoaded(false);
        }
    };

    // Recreate map when data changes
    useEffect(() => {
        if (!mapboxReady || typeof window === 'undefined') return;

        setMapError(null);
        setIsMapLoaded(false);
        const timer = setTimeout(createMap, 200);

        return () => {
            clearTimeout(timer);
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [selectedJourneyIndex, hasProductJourney, itemData, items, mapboxReady]);

    const getStepItemLabel = (item) => {
        const { key, label, unit, label_iso_to_name } = item;
        const countryName = label_iso_to_name ? toLower(countryCoordinates[label]?.name || countryCoordinates3[label]?.name || label) : label;
        const country = <h6 className="text-capitalize">{countryName}</h6>;

        const labels = {
            'country': country,
            'factory country': country,
            'fabric mill country': country,
            'fulfilment centre country': country,
            'transportation': <h6 className="text-capitalize">{toLower(label)}</h6>,
            'distance to next stage': <h6 className="text-capitalize">{label.toLowerCase()}{' ' + unit || ''}</h6>,
        }
        return labels[toLower(key)] || label;
    }

    if (hasProductJourney && typeof window !== 'undefined' && !mapboxReady && itemData.map_tool === 'mapbox') {
        return (
            <div className="product-content style_1">
                <div className="map-loading">Loading Mapbox GL...</div>
            </div>
        );
    }

    if (hasProductJourney) {
        const productJourney = itemData?.product_journey || items?.product_journey || items?.[0]?.product_journey;
        if (!productJourney || !Array.isArray(productJourney)) {
            return <div>No product journey data available</div>;
        }

        const selectedJourney = productJourney[selectedJourneyIndex];
        const visibleSteps = selectedJourney?.steps?.filter(isStepVisible) || [];

        return (
            <div className="product-content style_1">
                {productJourney.length > 1 && (
                    <div className="journey-selector" ref={dropdownRef}>
                        <div
                            className={`journey-dropdown-custom ${isDropdownOpen ? 'open' : ''}`}
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        >
                            <div className="selected-option">
                                <div className="option-content">
                                    <span className="option-text">
                                        {selectedJourney?.title || selectedJourney?.name || `Journey ${selectedJourneyIndex + 1}`}
                                    </span>
                                </div>
                                <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}></span>
                            </div>

                            {isDropdownOpen && (
                                <div className="dropdown-options">
                                    {productJourney.map((journey, index) => (
                                        <div
                                            key={index}
                                            className={`dropdown-option ${index === selectedJourneyIndex ? 'selected' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleJourneySelect(index);
                                            }}
                                        >
                                            <span className="option-text">
                                                {journey.title || journey.name || `Journey ${index + 1}`}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {selectedJourney?.steps && (
                    <div className="journey-map">
                        {
                            itemData.map_tool === 'mapbox' ?
                                <>
                                    <div ref={mapRef} className="mapbox-container" />
                                    {mapError && (
                                        <div className="map-error">
                                            <p>Map failed to load: {mapError}</p>
                                            <button onClick={() => {
                                                setMapError(null);
                                                setIsMapLoaded(false);
                                                createMap();
                                            }}>
                                                Retry
                                            </button>
                                        </div>
                                    )}
                                </> :
                                <div className="location-wrapper">
                                    <img src={selectedJourney.image} alt=""/>
                                </div>
                        }
                    </div>
                )}

                <div className={`carousel-wrapper ${visibleSteps?.length === 1 ? 'single-slide' : ''}`}>
                    {visibleSteps?.length > 0 ? (
                        <Swiper
                            key={selectedJourneyIndex}
                            slidesPerView={visibleSteps?.length === 1 ? 1 : 1.18}
                            spaceBetween={visibleSteps?.length === 1 ? 0 : 15}
                            navigation={visibleSteps?.length > 1}
                            centeredSlides={visibleSteps?.length === 1}
                            allowTouchMove={visibleSteps?.length > 1}
                            slidesOffsetBefore={20}
                            slidesOffsetAfter={20}
                            loop={false}
                            modules={[Pagination, Navigation]}
                            onSwiper={(swiper) => {
                                swiperRef.current = swiper;
                                setCurrentStepIndex(0);
                            }}
                            onSlideChange={(swiper) => {
                                hasUserNavigatedRef.current = true;
                                setCurrentStepIndex(swiper.activeIndex);
                            }}
                        >
                            {visibleSteps?.map((step, index) => (
                                <SwiperSlide key={index}>
                                    <div className="product-item" onClick={() => handleSlideClick(swiperRef.current)}>
                                        <div className="product-title">
                                            {step.title}
                                            {step.image && <img className="product-icon" src={step.image} alt="" />}
                                        </div>

                                        {!!step.info?.length && (
                                            <div className="product-info">
                                                {step.info?.map((item, infoIndex) => {
                                                    if (item.key.toLowerCase().includes('distance') && (item.label === '0.0' || item.label === '0')) {
                                                        return null;
                                                    }

                                                    return !!item?.label && (
                                                        <div key={infoIndex} className={`info-item ${item.width === 'full' ? 'full' : ''}`}>
                                                            <h5>{capitalize(item.key)}</h5>
                                                            <h6 className={
                                                                toLower(item.key) === 'factory' &&
                                                                item.label &&
                                                                !item.label.includes(' ') &&
                                                                item.label.length > 15
                                                                    ? 'single-long-word'
                                                                    : ''
                                                            }>
                                                                {getStepItemLabel(item)}
                                                            </h6>
                                                            {item.notification && <p className="notification">{item.notification}</p>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    ) : (
                        <div>No steps available for this journey</div>
                    )}
                </div>
            </div>
        );
    }

    // First version sliders display
    return (
        <>
            {items?.map((data, index) => {
                return (!!Object.keys(data)?.length && !!data.items?.length && (
                    <div key={index} className={`product-content style_1`}>
                        {data.name && (<p className="product-item-title">{data.name}</p>)}

                        <div className={`carousel-wrapper ${data.items?.length === 1 ? 'single-slide' : ''}`}>
                            {!!data.items?.length && (
                                <Swiper
                                    slidesPerView={data.items?.length === 1 ? 1 : 1.18}
                                    spaceBetween={data.items?.length === 1 ? 0 : 15}
                                    navigation={data.items?.length > 1}
                                    centeredSlides={data.items?.length === 1}
                                    loop={false}
                                    modules={[Pagination, Navigation]}
                                    onSwiper={(swiper) => {
                                        itemsSwiperRefs.current[index] = swiper;
                                    }}
                                    onSlideChange={(swiper) => setCurrentItemIndex(swiper.activeIndex)}
                                >
                                    {data?.items?.map((product, productIndex) => {
                                        const labelCheck = product.info.every(item => !item.label);

                                        return (!labelCheck && product?.display !== 'false' &&
                                            <SwiperSlide key={productIndex}>
                                                <div className="product-item" onClick={() => handleSlideClick(itemsSwiperRefs.current[index])}>
                                                    <div className="product-title">
                                                        {product.title}
                                                        <img className="product-icon" src={product.image} alt="" />
                                                    </div>

                                                    {!!product.info?.length && (
                                                        <div className="product-info">
                                                            {product.info?.map((item, infoIndex) => {
                                                                if (item.key.toLowerCase().includes('distance') && (item.label === '0.0' || item.label === '0')) {
                                                                    return null;
                                                                }

                                                                return !!item?.label && (
                                                                    <div key={infoIndex} className={`info-item ${item.width === 'full' ? 'full' : ''}`}>
                                                                        <h5>{capitalize(item.key)}</h5>
                                                                        <h6>{getStepItemLabel(item)}</h6>
                                                                        {item.notification && <p className="notification">{item.notification}</p>}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </SwiperSlide>
                                        );
                                    })}
                                </Swiper>
                            )}
                        </div>
                    </div>
                ));
            })}
        </>
    );
}
